import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Lock, ArrowRight, Info } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { TIER_COPY, STATUS_LABEL } from "@/lib/hooks";

const Step = ({ t, active, onPick }) => {
  const copy = TIER_COPY[t.tier];
  const done = t.status === "certified";
  return (
    <button
      type="button"
      aria-disabled={!t.unlocked}
      onClick={() => onPick(t)}
      data-testid={`tier-step-${t.tier}`}
      aria-pressed={active}
      className={`group relative flex-1 rounded-xl border p-5 text-left transition-[border-color,transform,background-color] ${active ? "border-brand-primary bg-card shadow-sm" : "border-border bg-card/60 hover:-translate-y-0.5 hover:border-brand-primary/60"} ${!t.unlocked ? "opacity-60 hover:translate-y-0" : ""}`}
    >
      <div className="flex items-start justify-between">
        <span className={`font-heading text-3xl font-black ${active ? "text-brand-primary" : "text-muted-foreground"}`}>{copy.mark}</span>
        {done ? (
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-accent text-brand-accent-fg"><Check className="h-4 w-4" /></span>
        ) : !t.unlocked ? <Lock className="h-4 w-4 text-muted-foreground" /> : !active ? <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1" /> : null}
      </div>
      <div className="mt-3 font-heading text-lg font-bold">{copy.label}</div>
      <p className="mt-1 text-sm text-muted-foreground">{copy.blurb}</p>
      <div className="mt-4 flex flex-col gap-1 text-xs">
        <span data-testid={`tier-status-${t.tier}`} className="font-semibold uppercase tracking-wider">{active ? "Viewing · " : ""}{STATUS_LABEL[t.status]}</span>
        <span className="font-mono text-[11px] text-muted-foreground">{t.blueprint.easy} easy · {t.blueprint.medium} medium · {t.blueprint.hard} hard</span>
      </div>
      {active && <div className="absolute inset-x-4 top-0 h-0.5 rounded-full bg-brand-primary" />}
    </button>
  );
};

const ORDER = ["foundation", "practitioner", "strategist"];

export const TierLadder = ({ tiers, current }) => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [msg, setMsg] = useState("");
  const pick = async (t) => {
    if (!t.unlocked) {
      const prev = TIER_COPY[ORDER[ORDER.indexOf(t.tier) - 1]]?.label;
      setMsg(`${TIER_COPY[t.tier].label} is locked. It opens when you earn your ${prev} certificate.`);
      return;
    }
    setMsg("");
    try {
      if (t.tier !== current) {
        await api.patch("/progress/tier", { tier: t.tier });
        await qc.invalidateQueries({ queryKey: ["progress"] });
      }
      navigate("/learn");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div>
      <div id="levels" data-testid="tier-ladder" className="flex scroll-mt-24 flex-col gap-3 md:flex-row">
        {tiers.map((t) => <Step key={t.tier} t={t} active={t.tier === current} onPick={pick} />)}
      </div>
      {msg && <p data-testid="tier-locked-message" role="status" className="mt-3 flex items-center gap-2 rounded-lg border border-brand-action/50 bg-brand-action/10 px-4 py-3 text-sm font-medium"><Info className="h-4 w-4 shrink-0" />{msg}</p>}
    </div>
  );
};
